import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _typeof from '@babel/runtime/helpers/typeof';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import * as React from 'react';
import { useId } from 'react-id-generator';
import styled from 'styled-components';
import { KEYS } from '../../constants/keys.js';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { getAdornment } from '../../utils/adornment.js';
import { bindRefs } from '../../utils/react.js';
import { noop } from '../../utils/util.js';
import { Checkbox } from '../Checkbox/Checkbox.js';
import { List } from '../List/List.js';
import { ListItem } from '../ListItem/ListItem.js';
import { Radio } from '../Radio/Radio.js';
import { LISTBOX_DATA_TESTIDS } from './constants.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["enableSelectAll", "endAdornment", "filterOptions", "id", "multi", "onFinalListItem", "onFirstListItem", "onSelectedChange", "optionHeight", "options", "renderOption", "scrollRef", "selectedOptions", "startAdornment"];
var SELECT_ALL = "__SELECT_ALL__";
var StyledAdornmentContainer = styled.span.withConfig({
  displayName: "Listbox__StyledAdornmentContainer",
  componentId: "sc-lzxtms-0"
})({
  display: "flex",
  marginInlineEnd: 10
});
var toggleArrayValue = function toggleArrayValue(arrayList, arrayValue) {
  return arrayList.includes(arrayValue) ? arrayList.filter(function (el) {
    return el !== arrayValue;
  }) : [].concat(_toConsumableArray(arrayList), [arrayValue]);
};
var Listbox = /*#__PURE__*/React.forwardRef(function Component(props, ref) {
  var enableSelectAll = props.enableSelectAll,
    endAdornment = props.endAdornment,
    _props$filterOptions = props.filterOptions,
    filterOptions = _props$filterOptions === void 0 ? function () {
      return true;
    } : _props$filterOptions,
    id = props.id,
    multi = props.multi,
    onFinalListItem = props.onFinalListItem,
    onFirstListItem = props.onFirstListItem,
    onSelectedChange = props.onSelectedChange,
    optionHeight = props.optionHeight,
    _props$options = props.options,
    optionsProp = _props$options === void 0 ? [] : _props$options,
    renderOption = props.renderOption,
    scrollRefProp = props.scrollRef,
    _props$selectedOption = props.selectedOptions,
    selectedOptions = _props$selectedOption === void 0 ? [] : _props$selectedOption,
    startAdornment = props.startAdornment,
    rest = _objectWithoutProperties(props, _excluded);
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var canSelectAll = multi && enableSelectAll;
  var selectAllOption = {
    value: SELECT_ALL
  };
  var options = canSelectAll ? [selectAllOption].concat(_toConsumableArray(optionsProp)) : optionsProp;
  var filteredOptions = options.filter(function (option, i) {
    return canSelectAll && i === 0 || filterOptions(option);
  });
  var filteredOptionsLength = filteredOptions.length;
  var isFiltered = filteredOptionsLength !== options.length;
  var hasStartIcon = _typeof(startAdornment) === "object";
  var hasEndIcon = _typeof(endAdornment) === "object";
  var _useId = useId(1, "listbox-"),
    _useId2 = _slicedToArray(_useId, 1),
    defaultId = _useId2[0];
  var listboxId = id || defaultId;
  var optionProps = React.useMemo(function () {
    return Array.from({
      length: filteredOptionsLength
    }, function (v, i) {
      return {
        id: "".concat(listboxId, "-option-").concat(i),
        ref: /*#__PURE__*/React.createRef()
      };
    });
  }, [filteredOptionsLength, listboxId]);
  var startAdornmentElement = function startAdornmentElement(index) {
    var _optionProps$index$re;
    return hasStartIcon && ((_optionProps$index$re = optionProps[index].ref) === null || _optionProps$index$re === void 0 ? void 0 : _optionProps$index$re.current.childNodes[1].childNodes[0].firstChild);
  };
  var endAdornmentElement = function endAdornmentElement(index) {
    var _optionProps$index$re2;
    return hasEndIcon && ((_optionProps$index$re2 = optionProps[index].ref) === null || _optionProps$index$re2 === void 0 ? void 0 : _optionProps$index$re2.current.childNodes[3].childNodes[0].firstChild);
  };
  var listRef = React.useRef();
  var scrollRef = scrollRefProp || listRef;
  var scrollToIndex = function scrollToIndex(nextIndex) {
    var listElem = scrollRef.current;
    var elem = optionProps[nextIndex].ref.current;
    var elemTop = elem.offsetTop - listElem.offsetTop;
    var elemBottom = elemTop + elem.offsetHeight;
    var shouldScrollDown = elemBottom >= listElem.offsetHeight + listElem.scrollTop;
    var shouldScrollUp = elemTop <= listElem.scrollTop;
    elem.focus();
    if (nextIndex === 0 && shouldScrollUp) {
      listElem.scrollTop = 0;
      return;
    }
    if (shouldScrollDown) {
      listElem.scrollTop = elem.offsetTop - listElem.offsetHeight + elem.offsetHeight;
    }
    if (shouldScrollUp) {
      listElem.scrollTop = elemTop;
    }
  };
  var handleSelect = function handleSelect(index) {
    var option = filteredOptions[index];
    if (!onSelectedChange || option.disabled) return;
    var nextSelectedOptions;
    if (option.value === SELECT_ALL) {
      nextSelectedOptions = optionsProp.length === selectedOptions.length ? [] : optionsProp;
    } else {
      nextSelectedOptions = multi ? toggleArrayValue(selectedOptions, option) : [option];
    }
    onSelectedChange(nextSelectedOptions);
  };
  var handleListItemKeyDown = function handleListItemKeyDown(e, index) {
    switch (e.key) {
      case KEYS.ARROW_UP:
        {
          if (index === 0) {
            if (onFirstListItem) {
              onFirstListItem();
              e.preventDefault();
            }
          } else {
            scrollToIndex(Math.max(index - 1, 0));
            e.preventDefault();
          }
          break;
        }
      case KEYS.ARROW_DOWN:
        {
          if (index === filteredOptions.length - 1) {
            if (onFinalListItem) {
              onFinalListItem();
              e.preventDefault();
            }
          } else {
            scrollToIndex(Math.min(index + 1, filteredOptions.length - 1));
            e.preventDefault();
          }
          break;
        }
      case KEYS.ARROW_RIGHT:
        {
          if (hasStartIcon && document.activeElement === startAdornmentElement(index)) {
            scrollToIndex(index);
          } else if (hasEndIcon) {
            endAdornmentElement(index).focus();
          }
          e.preventDefault();
          break;
        }
      case KEYS.ARROW_LEFT:
        {
          if (hasEndIcon && document.activeElement === endAdornmentElement(index)) {
            scrollToIndex(index);
          } else if (hasStartIcon) {
            startAdornmentElement(index).focus();
          }
          e.preventDefault();
          break;
        }
      case KEYS.ENTER:
        {
          if (document.activeElement !== endAdornmentElement(index) && document.activeElement !== startAdornmentElement(index) && !multi) {
            handleSelect(index);
          }
          break;
        }
      case KEYS.SPACE:
        {
          if (document.activeElement !== endAdornmentElement(index) && document.activeElement !== startAdornmentElement(index) && multi) {
            handleSelect(index);
          }
          e.preventDefault();
          break;
        }
    }
  };
  return /*#__PURE__*/jsx(List, _objectSpread(_objectSpread({}, rest), {}, {
    "aria-multiselectable": multi ? true : void 0,
    id: listboxId,
    onFocus: function onFocus(e) {
      if (e.target === listRef.current) optionProps[0].ref.current.focus();
      if (props.onFocus) props.onFocus(e);
    },
    ref: bindRefs(ref, listRef),
    role: "listbox",
    tabIndex: -1,
    children: filteredOptions.map(function (option, index) {
      var isSelectAll = canSelectAll && index === 0;
      var sharedListItemProps = {
        id: optionProps[index].id,
        key: optionProps[index].id,
        onClick: function onClick() {
          return handleSelect(index);
        },
        onKeyDown: function onKeyDown(e) {
          return handleListItemKeyDown(e, index);
        },
        ref: optionProps[index].ref,
        role: "option",
        tabIndex: -1
      };
      var sharedCheckboxProps = {
        as: "div",
        readOnly: true,
        role: "none"
      };
      if (isSelectAll) {
        var ariaSelected = selectedOptions.length === optionsProp.length;
        var indeterminate = selectedOptions.length > 0 && !ariaSelected;
        return !isFiltered ? /*#__PURE__*/jsxs(ListItem, _objectSpread(_objectSpread({
          "aria-live": "polite",
          "aria-selected": ariaSelected,
          border: true,
          hasEndAdornment: hasEndIcon,
          variant: optionHeight
        }, sharedListItemProps), {}, {
          children: [/*#__PURE__*/jsx(StyledAdornmentContainer, {
            children: /*#__PURE__*/jsx(Checkbox, _objectSpread({
              checked: ariaSelected,
              indeterminate: indeterminate
            }, sharedCheckboxProps))
          }), hasStartIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            "aria-label": "".concat(option.label, " ").concat(t("Listbox.startAdornmentAction")),
            "data-testid": LISTBOX_DATA_TESTIDS.startAdornmentContainer,
            children: getAdornment(startAdornment, {
              iconSize: 16
            })
          }), ariaSelected ? t("Listbox.deselectAll") : t("Listbox.selectAll"), hasEndIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            "aria-label": "".concat(option.label, " ").concat(t("Listbox.endAdornmentAction")),
            "data-testid": LISTBOX_DATA_TESTIDS.endAdornmentContainer,
            children: getAdornment(endAdornment, {
              iconSize: 16
            })
          })]
        })) : null;
      } else {
        var _option$variant;
        var _ariaSelected = selectedOptions.includes(option);
        return /*#__PURE__*/jsxs(ListItem, _objectSpread(_objectSpread(_objectSpread({}, option), sharedListItemProps), {}, {
          "aria-selected": _ariaSelected,
          ariaLabelledBy: "".concat(optionProps[index].id, "-content"),
          hasEndAdornment: hasEndIcon,
          variant: (_option$variant = option.variant) !== null && _option$variant !== void 0 ? _option$variant : optionHeight,
          children: [function () {
            if (multi) {
              return /*#__PURE__*/jsx(StyledAdornmentContainer, {
                children: /*#__PURE__*/jsx(Checkbox, _objectSpread({
                  checked: _ariaSelected
                }, sharedCheckboxProps))
              });
            } else if (startAdornment === "radio") {
              return /*#__PURE__*/jsx(StyledAdornmentContainer, {
                children: /*#__PURE__*/jsx(Radio, {
                  checked: _ariaSelected,
                  onChange: noop
                })
              });
            }
          }(), hasStartIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            "aria-label": "".concat(option.label, " ").concat(t("Listbox.startAdornmentAction")),
            "data-testid": LISTBOX_DATA_TESTIDS.startAdornmentContainer,
            children: getAdornment(startAdornment, {
              iconSize: 16
            })
          }), /*#__PURE__*/jsx("div", {
            id: "".concat(optionProps[index].id, "-content"),
            children: renderOption(option)
          }), hasEndIcon && /*#__PURE__*/jsx(StyledAdornmentContainer, {
            "aria-label": "".concat(option.label, " ").concat(t("Listbox.endAdornmentAction")),
            "data-testid": LISTBOX_DATA_TESTIDS.endAdornmentContainer,
            children: getAdornment(endAdornment, {
              iconSize: 16
            })
          })]
        }));
      }
    })
  }));
});
Listbox.displayName = "Listbox";

export { Listbox };
